/**
 * ContractTemplates.jsx – Vertragsvorlagen-Verwaltung
 * Admin kann Templates erstellen, bearbeiten, löschen und Variablen einfügen.
 */
import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Plus, Edit2, Trash2, Loader2, Eye, EyeOff, Copy, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIABLES = [
  { group: 'Firma (aus Standort-Profil)', vars: [
    { key: '{{firma_name}}', label: 'Firmenname' },
    { key: '{{firma_inhaber}}', label: 'Inhaber' },
    { key: '{{firma_adresse}}', label: 'Straße & Hausnr.' },
    { key: '{{firma_plz_ort}}', label: 'PLZ & Ort' },
    { key: '{{standort}}', label: 'Ort / Stadt' },
    { key: '{{firma_unterzeichner}}', label: 'Unterzeichner inkl. Titel' },
    { key: '{{firma_betriebsnummer}}', label: 'Betriebsnummer' },
    { key: '{{firma_steuernummer}}', label: 'Steuernummer' },
    { key: '{{firma_ust_id}}', label: 'USt-IdNr.' },
    { key: '{{firma_registergericht}}', label: 'Registergericht' },
    { key: '{{firma_hrb}}', label: 'HRB-Nummer' },
  ]},
  { group: 'Mitarbeiter', vars: [
    { key: '{{vorname}}', label: 'Vorname' },
    { key: '{{nachname}}', label: 'Nachname' },
    { key: '{{an_strasse}}', label: 'Straße & Hausnr.' },
    { key: '{{an_plz_ort}}', label: 'PLZ & Ort' },
    { key: '{{an_geburtsdatum}}', label: 'Geburtsdatum' },
  ]},
  { group: 'Vertragsdaten', vars: [
    { key: '{{eintrittsdatum}}', label: 'Eintrittsdatum' },
    { key: '{{job_bezeichnung}}', label: 'Tätigkeit / Position (auto)' },
    { key: '{{taetigkeit}}', label: 'Ausgeübte Tätigkeit' },
    { key: '{{berufsbezeichnung}}', label: 'Formale Berufsbezeichnung' },
    { key: '{{vergutung}}', label: 'Brutto-Vergütung' },
    { key: '{{wochenstunden}}', label: 'Std./Woche' },
    { key: '{{monatsstunden}}', label: 'Std./Monat' },
    { key: '{{arbeitstage_woche}}', label: 'Arbeitstage/Woche' },
    { key: '{{stundenlohn}}', label: 'Stundenlohn (€)' },
    { key: '{{monatslohn}}', label: 'Monatslohn (€)' },
    { key: '{{urlaubstage}}', label: 'Urlaubstage/Jahr' },
    { key: '{{urlaubstage_berechnet}}', label: 'Urlaub (anteilig berechnet)' },
    { key: '{{heutiges_datum}}', label: 'Heutiges Datum' },
  ]},
];

const CONTRACT_TYPES = [
  { value: 'vz', label: 'Vollzeit' },
  { value: 'tz', label: 'Teilzeit' },
  { value: 'minijob', label: 'Minijob / Aushilfe' },
  { value: 'custom', label: 'Sonstiges' },
];

export default function ContractTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [editModal, setEditModal] = useState(null); // null | 'new' | template object
  const [form, setForm] = useState({ name: '', contract_type: 'vz', template_text: '' });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // null | template id
  const textareaRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contracts/templates`, { headers });
      setTemplates(r.data || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/contracts/templates/seed`, {}, { headers });
      toast.success(`${r.data.length} Start-Templates eingespielt!`);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally { setSeeding(false); }
  };

  const openEdit = (t) => {
    setForm(t ? { name: t.name, contract_type: t.contract_type, template_text: t.template_text } : { name: '', contract_type: 'vz', template_text: '' });
    setEditModal(t || 'new');
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.template_text.trim()) {
      toast.error('Name und Vertragstext sind Pflicht'); return;
    }
    setSaving(true);
    try {
      if (editModal === 'new') {
        await axios.post(`${API}/contracts/templates`, form, { headers });
        toast.success('Vorlage erstellt');
      } else {
        await axios.put(`${API}/contracts/templates/${editModal.id}`, form, { headers });
        toast.success('Vorlage aktualisiert');
      }
      setEditModal(null);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`"${t.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/contracts/templates/${t.id}`, { headers });
      toast.success('Gelöscht');
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const insertVariable = (varKey) => {
    const ta = textareaRef.current;
    if (!ta) {
      setForm(f => ({ ...f, template_text: f.template_text + varKey }));
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = form.template_text.slice(0, start);
    const after = form.template_text.slice(end);
    const newText = before + varKey + after;
    setForm(f => ({ ...f, template_text: newText }));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + varKey.length, start + varKey.length);
    }, 10);
  };

  const handleUpdateDefaults = async () => {
    if (!window.confirm('Standard-Vorlagen (Vollzeit/Teilzeit und Aushilfe/Minijob) werden auf die neueste Version aktualisiert. Eigene Änderungen gehen verloren. Fortfahren?')) return;
    try {
      const r = await axios.post(`${API}/contracts/templates/update-defaults`, {}, { headers });
      toast.success(r.data.message || 'Vorlagen aktualisiert');
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              Vertragsvorlagen
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Einmalig einrichten – automatisch für alle neuen Mitarbeiter genutzt
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {templates.length === 0 && (
              <Button variant="outline" onClick={handleSeed} disabled={seeding}>
                {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Start-Templates einspielen
              </Button>
            )}
            {templates.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleUpdateDefaults} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="update-defaults-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Standard aktualisieren
              </Button>
            )}
            <Button onClick={() => openEdit(null)}>
              <Plus className="h-4 w-4 mr-1" /> Neue Vorlage
            </Button>
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : templates.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center space-y-3">
              <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto" />
              <p className="text-muted-foreground font-medium">Noch keine Vertragsvorlagen</p>
              <p className="text-sm text-muted-foreground/70">Klicke "Start-Templates einspielen" um die JET-Vorlagen zu laden.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => (
              <Card key={t.id} className="shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-foreground">{t.name}</h3>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {CONTRACT_TYPES.find(c => c.value === t.contract_type)?.label || t.contract_type}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => setPreview(preview === t.id ? null : t.id)}>
                        {preview === t.id ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(t)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t.template_text.slice(0, 120)}...
                  </p>
                  {preview === t.id && (
                    <div className="bg-muted/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <pre className="text-xs text-foreground whitespace-pre-wrap font-sans">{t.template_text}</pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
        <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogTitle>{editModal === 'new' ? 'Neue Vorlage' : 'Vorlage bearbeiten'}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 gap-4">
            {/* Name + Typ */}
            <div className="grid grid-cols-2 gap-4 shrink-0">
              <div className="space-y-1.5">
                <Label>Name der Vorlage *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="z.B. Vollzeit VZ 2025" />
              </div>
              <div className="space-y-1.5">
                <Label>Vertragstyp</Label>
                <select value={form.contract_type} onChange={e => setForm(f => ({...f, contract_type: e.target.value}))}
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                  {CONTRACT_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Zwei-Spalten-Editor */}
            <div className="flex-1 grid grid-cols-3 gap-4 min-h-0">
              {/* Textarea */}
              <div className="col-span-2 space-y-1.5 flex flex-col min-h-0">
                <Label className="shrink-0">Vertragstext * (&#123;&#123;variablen&#125;&#125; werden beim Generieren ersetzt)</Label>
                <textarea
                  ref={textareaRef}
                  value={form.template_text}
                  onChange={e => setForm(f => ({...f, template_text: e.target.value}))}
                  className="flex-1 w-full rounded-md border bg-background px-3 py-2 text-sm font-mono
                    resize-none focus:outline-none focus:ring-2 focus:ring-primary min-h-0"
                  placeholder="Vertragstext hier einfügen oder tippen..."
                  spellCheck={false}
                />
                <p className="text-xs text-muted-foreground shrink-0">
                  Tipp: Füge bestehenden Vertrag per Strg+V ein. Klicke dann rechts auf eine Variable um sie einzufügen.
                </p>
              </div>

              {/* Variable-Sidebar */}
              <div className="space-y-3 overflow-y-auto">
                <p className="text-sm font-semibold text-foreground">Variablen einfügen</p>
                <p className="text-xs text-muted-foreground">Klick = an Cursor-Position einfügen</p>
                {VARIABLES.map(group => (
                  <div key={group.group} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{group.group}</p>
                    {group.vars.map(v => (
                      <button key={v.key} onClick={() => insertVariable(v.key)}
                        className="w-full text-left px-2.5 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/20 text-xs transition-colors group">
                        <span className="font-mono text-primary">{v.key}</span>
                        <span className="text-muted-foreground ml-2 group-hover:text-foreground">{v.label}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t gap-2 shrink-0">
            <Button variant="outline" onClick={() => setEditModal(null)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editModal === 'new' ? 'Vorlage erstellen' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
